import React, { useEffect, useState } from 'react';

/**
 * Edit campaign budget and per-creator fees. Calls onSave({ budget_inr, row_fees }).
 */
export default function PerfHubEconomicsModal({
  open,
  onClose,
  onSave,
  busy,
  campaignName,
  initialBudget,
  rows = [],
}) {
  const [budgetDraft, setBudgetDraft] = useState('');
  const [feeDrafts, setFeeDrafts] = useState({});

  useEffect(() => {
    if (!open) return;
    setBudgetDraft(
      initialBudget != null && initialBudget !== '' ? String(initialBudget) : ''
    );
    const next = {};
    (rows || []).forEach((r) => {
      const id = r.id || r.row_id;
      if (!id) return;
      const fee = r.fee_inr;
      next[id] = fee != null && fee !== '' ? String(fee) : '';
    });
    setFeeDrafts(next);
  }, [open, initialBudget, rows]);

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const budgetTrim = budgetDraft.trim();
    let budget_inr;
    if (budgetTrim === '') {
      budget_inr = null;
    } else {
      const n = Number(budgetTrim.replace(/,/g, ''));
      if (!Number.isFinite(n) || n < 0) return;
      budget_inr = n;
    }

    const row_fees = (rows || [])
      .map((r) => {
        const id = r.id || r.row_id;
        if (!id) return null;
        const raw = (feeDrafts[id] ?? '').trim();
        if (raw === '') return { row_id: id, fee_inr: null };
        const n = Number(raw.replace(/,/g, ''));
        if (!Number.isFinite(n) || n < 0) return null;
        return { row_id: id, fee_inr: n };
      })
      .filter(Boolean);

    onSave({ budget_inr, row_fees });
  };

  return (
    <div className="ph-econ-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="ph-econ-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ph-econ-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ph-econ-modal-head">
          <h3 id="ph-econ-modal-title">Campaign spend</h3>
          {campaignName && <p className="ph-econ-modal-sub">{campaignName}</p>}
          <button
            type="button"
            className="ph-econ-modal-close"
            aria-label="Close"
            onClick={onClose}
            disabled={busy}
          >
            ×
          </button>
        </header>

        <form className="ph-econ-modal-form" onSubmit={handleSubmit}>
          <div className="ph-econ-modal-field">
            <label className="ph-field-label" htmlFor="ph-econ-budget">
              Total campaign budget (INR)
            </label>
            <input
              id="ph-econ-budget"
              className="ph-input ph-input-full"
              inputMode="decimal"
              placeholder="e.g. 50000"
              value={budgetDraft}
              onChange={(e) => setBudgetDraft(e.target.value)}
              disabled={busy}
              autoComplete="off"
            />
            <p className="ph-econ-modal-hint">
              Used when you don’t enter per-creator fees. Clear the field to remove the budget.
            </p>
          </div>

          {rows.length > 0 && (
            <div className="ph-econ-modal-fees">
              <h4 className="ph-econ-modal-fees-title">Per-creator fees (optional)</h4>
              <p className="ph-econ-modal-hint">
                If any fee is set, total spend uses the <strong>sum of fees</strong> instead of the
                campaign budget.
              </p>
              <div className="ph-econ-fee-table-wrap">
                <table className="funds-table enhanced ph-econ-fee-table">
                  <thead>
                    <tr>
                      <th>Creator</th>
                      <th>Fee (INR)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const id = r.id || r.row_id;
                      return (
                        <tr key={id}>
                          <td>
                            <strong>@{r.instagram_handle}</strong>
                          </td>
                          <td>
                            <input
                              className="ph-input ph-econ-fee-input"
                              inputMode="decimal"
                              placeholder="—"
                              value={feeDrafts[id] ?? ''}
                              onChange={(e) =>
                                setFeeDrafts((prev) => ({ ...prev, [id]: e.target.value }))
                              }
                              disabled={busy}
                              aria-label={`Fee for ${r.instagram_handle}`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <footer className="ph-econ-modal-footer">
            <button type="button" className="btn-outline" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="btn-primary ph-btn-glow" disabled={busy}>
              {busy ? 'Saving…' : 'Save & refresh ROI'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
